import { clamp, damp, normalizePointer } from "./utils.js";

/**
 * Mouse/touchpad input layer. It adds drag rotation and wheel dolly on top of
 * the semantic gesture state, so either input method can be used at any time.
 */
export class MouseController {
  constructor(
    element,
    {
      minimumDistance = 4.5,
      maximumDistance = 13,
      dragYawScale = 5.6,
      dragPitchScale = 4.2,
      wheelScale = 0.0034,
      maximumPitch = 1.4,
    } = {},
  ) {
    this.element = element;
    this.minimumDistance = minimumDistance;
    this.maximumDistance = maximumDistance;
    this.dragYawScale = dragYawScale;
    this.dragPitchScale = dragPitchScale;
    this.wheelScale = wheelScale;
    this.maximumPitch = maximumPitch;
    this.rotationSensitivity = 1;
    this.pointer = { x: 0, y: 0 };
    this.rotation = { x: 0, y: 0 };
    this.angularVelocity = { x: 0, y: 0 };
    this.dollyOffset = 0;
    this.targetDollyOffset = 0;
    this.isDragging = false;
    this.activePointerId = null;
    this.lastPointer = null;

    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handlePointerLeave = this.handlePointerLeave.bind(this);
    this.handleWheel = this.handleWheel.bind(this);

    element.addEventListener("pointerdown", this.handlePointerDown);
    element.addEventListener("pointermove", this.handlePointerMove);
    element.addEventListener("pointerup", this.handlePointerUp);
    element.addEventListener("pointercancel", this.handlePointerUp);
    element.addEventListener("pointerleave", this.handlePointerLeave);
    element.addEventListener("wheel", this.handleWheel, { passive: false });
  }

  setRotationSensitivity(value) {
    this.rotationSensitivity = clamp(value, 0.5, 2);
    return this.rotationSensitivity;
  }

  handlePointerDown(event) {
    if (event.button !== 0) {
      return;
    }
    this.isDragging = true;
    this.activePointerId = event.pointerId;
    this.lastPointer = {
      x: event.clientX,
      y: event.clientY,
      timestamp: event.timeStamp,
    };
    this.angularVelocity.x = 0;
    this.angularVelocity.y = 0;
    this.element.setPointerCapture?.(event.pointerId);
    this.element.dataset.dragging = "true";
    event.preventDefault();
  }

  handlePointerMove(event) {
    this.pointer = normalizePointer(event, this.element);
    if (
      !this.isDragging ||
      event.pointerId !== this.activePointerId ||
      !this.lastPointer
    ) {
      return;
    }

    const bounds = this.element.getBoundingClientRect();
    const deltaX = event.clientX - this.lastPointer.x;
    const deltaY = event.clientY - this.lastPointer.y;
    const elapsed = clamp(
      (event.timeStamp - this.lastPointer.timestamp) / 1000,
      1 / 120,
      0.1,
    );
    const yawDelta =
      deltaX /
      Math.max(bounds.width, 1) *
      this.dragYawScale *
      this.rotationSensitivity;
    const pitchDelta =
      deltaY /
      Math.max(bounds.height, 1) *
      this.dragPitchScale *
      this.rotationSensitivity;

    this.rotation.x = clamp(
      this.rotation.x + pitchDelta,
      -this.maximumPitch,
      this.maximumPitch,
    );
    this.rotation.y += yawDelta;
    this.angularVelocity.x = clamp(
      this.angularVelocity.x * 0.35 + pitchDelta / elapsed * 0.65,
      -3.2,
      3.2,
    );
    this.angularVelocity.y = clamp(
      this.angularVelocity.y * 0.35 + yawDelta / elapsed * 0.65,
      -4.2,
      4.2,
    );
    this.lastPointer = {
      x: event.clientX,
      y: event.clientY,
      timestamp: event.timeStamp,
    };
  }

  handlePointerUp(event) {
    if (event.pointerId !== this.activePointerId) {
      return;
    }
    if (this.element.hasPointerCapture?.(event.pointerId)) {
      this.element.releasePointerCapture(event.pointerId);
    }
    this.isDragging = false;
    this.activePointerId = null;
    this.lastPointer = null;
    delete this.element.dataset.dragging;
  }

  handlePointerLeave() {
    if (!this.isDragging) {
      this.pointer.x = 0;
      this.pointer.y = 0;
    }
  }

  handleWheel(event) {
    if (event.ctrlKey) {
      return;
    }
    event.preventDefault();
    const modeScale = event.deltaMode === 1
      ? 16
      : event.deltaMode === 2
        ? Math.max(this.element.clientHeight, 1)
        : 1;
    const delta = clamp(event.deltaY * modeScale, -480, 480);
    this.targetDollyOffset = clamp(
      this.targetDollyOffset + delta * this.wheelScale,
      -3.4,
      3.4,
    );
  }

  update(delta, gestureInteraction) {
    const safeDelta = clamp(delta, 0, 0.05);
    if (!this.isDragging) {
      this.rotation.x = clamp(
        this.rotation.x + this.angularVelocity.x * safeDelta,
        -this.maximumPitch,
        this.maximumPitch,
      );
      this.rotation.y += this.angularVelocity.y * safeDelta;
      this.angularVelocity.x = damp(
        this.angularVelocity.x,
        0,
        3.8,
        safeDelta,
      );
      this.angularVelocity.y = damp(
        this.angularVelocity.y,
        0,
        3.8,
        safeDelta,
      );
    }
    this.dollyOffset = damp(
      this.dollyOffset,
      this.targetDollyOffset,
      8,
      safeDelta,
    );

    return {
      pointer: { ...this.pointer },
      rotation: {
        x: clamp(
          gestureInteraction.rotation.x + this.rotation.x,
          -this.maximumPitch,
          this.maximumPitch,
        ),
        y: gestureInteraction.rotation.y + this.rotation.y,
      },
      camera: {
        distance: clamp(
          gestureInteraction.camera.distance + this.dollyOffset,
          this.minimumDistance,
          this.maximumDistance,
        ),
      },
    };
  }

  dispose() {
    this.element.removeEventListener("pointerdown", this.handlePointerDown);
    this.element.removeEventListener("pointermove", this.handlePointerMove);
    this.element.removeEventListener("pointerup", this.handlePointerUp);
    this.element.removeEventListener("pointercancel", this.handlePointerUp);
    this.element.removeEventListener("pointerleave", this.handlePointerLeave);
    this.element.removeEventListener("wheel", this.handleWheel);
    delete this.element.dataset.dragging;
  }
}
